import React, { useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import dayjs from "dayjs"
import { X } from "react-feather"
import { Controller, Path, useForm, useWatch } from "react-hook-form"

import API from "api"
import { useLanguage } from "@languages/index"

import { getServiceName } from "constants/service/service"
import URLS from "constants/urls"
import Button from "components/button"
import InputDate from "components/inputDate"
import LoadingText from "components/loadingText"
import TextInputField from "components/textInput"
import HELPER_REACT_HOOK_FORM from "helpers/reactHookForm"
import useAppState from "hooks/useAppState"
import useGetBrand from "hooks/useGetBrand/useGetBrand"
import { TaxInvoiceFormProps } from "./overlayBrandTaxInvoiceInfoForm.types"

export interface OverlayBrandTaxInvoiceInfoFormResult {
  isSubmitted: boolean;
  data: {
    brand: {
      businessName: string | null;
      businessCeoName: string | null;
      businessNumber: string | null;
      businessEmail: string | null;
    },
    campaignPayment: {
      taxInvoiceScheduledDate: string | null;
      depositScheduledDate: string | null;
    }
  }
}


const OverlayBrandTaxInvoiceInfoForm = (props: {
  onComplete: (result: OverlayBrandTaxInvoiceInfoFormResult) => void,
}) => {
  const { state, actions } = useAppState(['auth', 'brand', 'preferences'])

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.overlayBrandTaxInvoiceInfoForm }

  const taxInvoiceFormMethods = useForm<TaxInvoiceFormProps>();
  const [
    taxInvoiceScheduledDate, 
    depositScheduledDate,
  ] = useWatch({
    control: taxInvoiceFormMethods.control,
    name: [
      'campaignPaymentData.taxInvoiceScheduledDate',
      'campaignPaymentData.depositScheduledDate',
    ]
  });

  const { data:brand, refetch:refetchBrand } = useGetBrand();

  const [initializedStates, setInitializedStates] = useState({
    isBrandRefetched: false,
  });
  const isInitialized = useMemo(() => (
    Object.values(initializedStates).every((value) => value)
  ), [initializedStates]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmittingState] = useState(isSubmittingRef.current);

  /********************************************************************
   **  STATE FUNCTIONS
   ********************************************************************/
  const setIsSubmitting = (newValue:boolean) => {
    isSubmittingRef.current = newValue;
    setIsSubmittingState(newValue);
  }



  /********************************************************************
   **  useEffects
   ********************************************************************/

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, []);

  useEffect(() => {
    async function initializeStates() {
      onChangeTaxInvoiceScheduledDate(dayjs().format('YYYY-MM-DD'))

      const brandResponse = await refetchBrand();
      if (brandResponse?.isSuccess) {
        taxInvoiceFormMethods.setValue('brandData.businessName', brandResponse.data.businessName)
        taxInvoiceFormMethods.setValue('brandData.businessCeoName', brandResponse.data.businessCeoName)
        taxInvoiceFormMethods.setValue('brandData.businessNumber', brandResponse.data.businessNumber)
        const emails = brandResponse.data.businessEmail != null ? (brandResponse.data.businessEmail).split(',') : []
        taxInvoiceFormMethods.setValue('brandData.businessEmail1', emails[0] ?? '')
        taxInvoiceFormMethods.setValue('brandData.businessEmail2', emails[1] ?? '')
        setInitializedStates((prev) => ({
          ...prev,
          isBrandRefetched: true,
        }))
      }

      await taxInvoiceFormMethods.trigger();
    }
    initializeStates()
  }, [])

  /**
   * - 세금계산서 발급 날짜: 선택 가능할 경우 날짜는 오늘 ~  다음 달 말일까지
   * - 입금 예정일: 
   *   - 기본값(공통) - 세금계산서 발행 날짜 +7일
   *   - 선택 가능한 날짜 :
   *     - 세금계선서 발급 날짜 변경 불가능 브랜드 - 세금계산서 발행 날짜 ~ 세금계산서 발행일로부터 7일 이내
   *     - 세금계선서 발급 날짜 변경 가능 브랜드 - 세금계산서 발행 날짜 ~ 세금계산서 발행 날짜의 다음 달 말일
   */
  const dateRanges = useMemo(() => {
    const output = {
      taxInvoiceScheduledDateRange: { min: undefined, max: undefined },
      depositScheduledDate: {min: undefined, max: undefined},
    };
    if (taxInvoiceScheduledDate == null) {
      return output;
    }

    output.taxInvoiceScheduledDateRange.min = dayjs().startOf('day').toDate();
    output.taxInvoiceScheduledDateRange.max = dayjs().add(1, 'month').endOf('month').endOf('day').toDate();

    output.depositScheduledDate.min = dayjs(taxInvoiceScheduledDate).startOf('day').toDate();
    if (brand?.businessCanEditInvoiceDate === 1) {
      output.depositScheduledDate.max = dayjs(taxInvoiceScheduledDate).add(1, 'month').endOf('month').startOf('day').toDate();
    } else {
      output.depositScheduledDate.max = dayjs(taxInvoiceScheduledDate).add(7, 'day').endOf('day').toDate();
    }

    return output
  }, [taxInvoiceScheduledDate, depositScheduledDate, brand?.businessCanEditInvoiceDate])

  /********************************************************************
   **  REQUESTS
   ********************************************************************/

  const submit = async () => {
    if (isSubmittingRef.current) {
      actions.showModal({
        text: languageTexts.is_requesting,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const brandData = taxInvoiceFormMethods.getValues().brandData;
      const paramsBrandUpdate = {
        brandToken: state.auth.token,
        businessName: brandData.businessName,
        businessCeoName: brandData.businessCeoName,
        businessNumber: brandData.businessNumber,
        businessEmail: getBusinessEmail(),
      };
      await API.Brand.update(paramsBrandUpdate);

      if (props.onComplete) {
        const campaignPaymentData = taxInvoiceFormMethods.getValues().campaignPaymentData;
        props.onComplete({
          isSubmitted: true,
          data: {
            brand: paramsBrandUpdate,
            campaignPayment: {
              taxInvoiceScheduledDate: campaignPaymentData.taxInvoiceScheduledDate,
              depositScheduledDate: campaignPaymentData.depositScheduledDate,
            }
          }
        });
        return;
      }
      setIsSubmitting(false);
    } catch (error) {
      actions.showModal({
        text: error.message,
      });
      setIsSubmitting(false);
    }
  }



  /********************************************************************
   **  HELPER FUNCTIONS
   ********************************************************************/

  function getBusinessEmail () {
    return [
      taxInvoiceFormMethods.getValues('brandData.businessEmail1'),
      taxInvoiceFormMethods.getValues('brandData.businessEmail2')
    ].filter(email => email != null && email.trim().length > 0).join(',')
  }

  /********************************************************************
   **  FORM FUNCTIONS
   ********************************************************************/

  async function validate(params: {
    showError?: boolean
  }) {
    // 필수 입력값 확인
    const isFormValid = await taxInvoiceFormMethods.trigger();
    if (!isFormValid) {
      const errorMessageText = HELPER_REACT_HOOK_FORM.getFormErrorMessage(
        taxInvoiceFormMethods.formState.errors, 
        {
          'brandData.businessName': languageTexts.index_text_business_name,
          'brandData.businessCeoName': languageTexts.index_text_business_ceo_name,
          'brandData.businessNumber': languageTexts.index_text_business_number,
          'brandData.businessEmail1': languageTexts.index_text_business_email,
          'campaignPaymentData.taxInvoiceScheduledDate': languageTexts.index_text_tax_invoice_date_title,
          'campaignPaymentData.depositScheduledDate': languageTexts.index_text_deposit_date_title,
        }
      )
      if (errorMessageText.length > 0) {
        params?.showError && actions.showModal({
          hideCloseButton: true,
          text: errorMessageText,
        })
      }
      return false;
    }

    // 사업자등록번호 10자리, 숫자만
    const businessNumber = taxInvoiceFormMethods.getValues('brandData.businessNumber');
    const isBusinessNumberValid = businessNumber?.length === 10 && businessNumber?.match(/^[0-9]+$/);
    if (!isBusinessNumberValid) {
      params?.showError && actions.showModal({
        text: languageTexts.index_text_error_business_number,
      });
      return false;
    }

    // 이메일은 1~2개만 가능. 편법 입력 방지 (',' 이용 여러개 입력 등)
    const emails = getBusinessEmail();
    const countSymbol = (text, symbol) => (text.split(symbol).length - 1);
    const hasValidEmailsCount = (
      countSymbol(emails, '@') <= 2 &&
      countSymbol(emails, ',') <= 1
    );
    if (!hasValidEmailsCount) {
      params?.showError && actions.showModal({
        text: languageTexts.index_text_error_emails_count,
      });
      return false;
    }

    return true;
  }
  
  
  /********************************************************************
   **  EVENT HANDLERS
   ********************************************************************/

  function onChangeTaxInvoiceScheduledDate (dateText:string) {
    const newDepositScheduledDate = dayjs(dateText).add(7, 'day').format('YYYY-MM-DD');
    taxInvoiceFormMethods.setValue('campaignPaymentData.taxInvoiceScheduledDate', dateText)
    taxInvoiceFormMethods.setValue('campaignPaymentData.depositScheduledDate', newDepositScheduledDate)
  }

  async function onSubmit () {
    const isValid = await validate({ showError: true });
    if (!isValid) {
      return;
    }

    const isWarningsAgreed = await checkUserAgreedWarnings();
    if (!isWarningsAgreed) {
      return;
    }

    submit();
  }

  async function checkUserAgreedWarnings () {
    const warnings = [
      {
        enabled: brand.businessEmail != getBusinessEmail(),
        title: languageTexts.index_text_warning_1_title,
        text: languageTexts.index_text_warning_1_description,
      }
    ];

    for (let warning of warnings) {
      if (!warning.enabled) {
        continue;
      }
      const isAgreed = await new Promise<boolean>(
        (resolve, reject) => {
          actions.showModal({
            hideCloseButton: true,
            title: warning.title,
            text: warning.text,
            confirm: () => { resolve(true); },
            cancel: () => { resolve(false); }
          })
        }
      )
      if (!isAgreed) {
        return false;
      }
    }
    return true;
  }


  /********************************************************************
   **  RENDERERS
   ********************************************************************/

  const CautionModal = (componentProps:{
    [key:string]:any,
    onConfirm:() => void,
    onCancel:() => void,
  }) => {
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);

    return (
      <div className="flex flex-col justify-center align-center p-8 bg-white">
        <div className="content flex flex-col">
          <span className="font-bold text-[22px] text-black">
            {/* {languageTexts.index_text_5} */}
          </span>
        </div>
        <div className="mt-4 text-[14px] text-black whitespace-pre-wrap">
          {/* {languageTexts.index_text_6} */}
        </div>
        <div className="mt-7 flex flex-row gap-[10px]">
          {/* <Checkbox
            checked={doNotShowAgain}
            text={languageTexts.index_text_7}
            onClick={() => {
              setDoNotShowAgain(!doNotShowAgain);
            }}
          /> */}
        </div>
        <div className="mt-6 buttons flex gap-1.5">
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            text={languageTexts.cancel}
            onClick={() => {
              componentProps.onCancel();
            }}
          />
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            buttonColor={"BLACK"}
            textColor={"WHITE"}
            text={languageTexts.confirm}
            onClick={() => {
              if (doNotShowAgain) {
                actions.setPreferences({
                  isMatchRatingSettingCautionModalDisabled: true,
                })
              }
              componentProps.onConfirm();
            }}
          />
        </div>
      </div>
    )
  }


  const viewClassNames = {
    sectionTitle: classNames([
      "text-[15px] font-bold"
    ])
  }

  const renderHeader = (params: {
    titleText:string
  }) => {
    return (
      <div className="pt-[50px] px-[20px]">
        <div className="text-[20px] font-bold">
          {params.titleText}
        </div>
        <div
          className="absolute right-0 top-0 p-[30px] cursor-pointer"
          onClick={() => {
            props.onComplete({
              isSubmitted: false,
              data: {
                brand: {
                  businessName: null,
                  businessCeoName: null,
                  businessNumber: null,
                  businessEmail: null,
                },
                campaignPayment: {
                  taxInvoiceScheduledDate: null,
                  depositScheduledDate: null,
                }
              }
            });
            actions.closeOverlays();
          }}
        >
          <X strokeWidth={1} />
        </div>
      </div>
    )
  }

  const renderInputDate = (params: {
    key:any,
    classNameContainer:string|null,
    titleText:string,
    minDate:string|null,
    maxDate:string|null,
    formKey: Path<TaxInvoiceFormProps>,
    isRequired: boolean,
    isEnabled:boolean|null,
  }) => (
    <div key={params.key} className={classNames([params.classNameContainer])}>
      <div className={viewClassNames.sectionTitle}>
        {params.titleText}
      </div>
      <div className="mt-[10px]">
        <Controller
          name={params.formKey}
          control={taxInvoiceFormMethods.control}
          rules={{ required: params.isRequired }}
          render={({ field }) => (
            <InputDate
              placeholder={languageTexts.input}
              minDate={params.minDate}
              maxDate={params.maxDate}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                if (params.formKey === 'campaignPaymentData.taxInvoiceScheduledDate') {
                  onChangeTaxInvoiceScheduledDate(value);
                }
              }}
              isEnabled={params.isEnabled}
            />
          )}
        />
      </div>
    </div>
  );

  const renderInputText = (params: {
    key:any,
    titleText:string,
    formKey: Path<TaxInvoiceFormProps>,
    isRequired: boolean,
  }) => (
    <div key={params.key} className="mt-[20px]">
      <div className={viewClassNames.sectionTitle}>
        {params.titleText}
      </div>
      <div className="mt-[10px]">
        <Controller
          name={params.formKey}
          control={taxInvoiceFormMethods.control}
          rules={{ required: params.isRequired }}
          render={({ field }) => (
            <TextInputField
              placeholder={languageTexts.input}
              value={field.value ?? ''}
              onChangeText={(text) => {
                if (params.formKey === 'brandData.businessNumber') {
                  if (!/^[0-9]+$/.test(text) || text.length > 10) {
                    return;
                  }
                }
                field.onChange(text);
              }}
            />
          )}
        />
      </div>
    </div>
  )

  const renderDocumentDownloadButtons = () => (
    <div className="mt-[40px] grid grid-cols-2 gap-[10px]">
      {[
        {
          'text': languageTexts._index_text_download_business_registration_certificate(getServiceName()),
          'url': URLS.CDN.BRAND.DOCUMENTS.URL + '/business_registration_certificate.pdf'
        },
        {
          'text': languageTexts._index_text_download_copy_of_bankbook(getServiceName()),
          'url': URLS.CDN.BRAND.DOCUMENTS.URL + '/copy_of_bankbook.pdf'
        },
      ].map((item, index) => {
        return (
          <a key={index} href={item.url} target="_blank" rel="noopener noreferrer"
            className="py-[20px] flex flex-col items-center border"
          >
            <div className="text-[13px] font-bold whitespace-pre-wrap text-center">
              {item.text}
            </div>
            <img
              className="w-[24px] h-[24px]"
              src="/assets/icons/ic-download-simple.svg"
            />
          </a>
        )
      })}
    </div>
  )

  return (
    <div
      className={classNames([
        "relative pb-[50px] max-h-[80vh] bg-white rounded-lg shadow-md",
        isInitialized ? "overflow-scroll" : "overflow-clip"
      ])}
      style={{ width: `${Math.min(windowWidth, 480)}px` }} 
    >
      {!isInitialized && (
        <div
          className="absolute top-0 w-full h-full flex justify-center items-center z-[1]"
          style={{ backgroundColor: 'rgb(225 225 225/ 0.75)' }}
        >
          <LoadingText
            text={'Loading'}
            render={(text) => (
              <div className="p-2 w-[180px] rounded-full text-center text-xl font-bold text-[#333] select-none">
                {text}
              </div>
            )}
          />
        </div>
      )}

      {renderHeader({
        titleText: languageTexts.index_text_title,
      })}

      <div className="mt-[34px] px-[20px]">

        {/* 세금계산서 발급 날짜, 입금 예정일 */}
        {[
          {
            classNameContainer: null,
            titleText: languageTexts.index_text_tax_invoice_date_title,
            minDate: dateRanges.taxInvoiceScheduledDateRange.min,
            maxDate: dateRanges.taxInvoiceScheduledDateRange.max,
            formKey: "campaignPaymentData.taxInvoiceScheduledDate" as Path<TaxInvoiceFormProps>,
            isRequired: true,
            isEnabled: brand?.businessCanEditInvoiceDate === 1,
          },
          {
            classNameContainer: "mt-[30px]",
            titleText: languageTexts.index_text_deposit_date_title,
            minDate: dateRanges.depositScheduledDate.min,
            maxDate: dateRanges.depositScheduledDate.max,
            formKey: "campaignPaymentData.depositScheduledDate" as Path<TaxInvoiceFormProps>,
            isRequired: true,
            isEnabled: true,
          }
        ].map((datum, i) => renderInputDate({ key:i, ...datum }))}

        {/* 사업자등록증, 통장사본 다운로드 */}
        {renderDocumentDownloadButtons()}

        {/* 사업자등록증의 회사명, 대표자명, 사업자등록번호, 세금계산서용 이메일 */}
        {[
          {
            titleText: languageTexts.index_text_business_name,
            formKey: 'brandData.businessName' as Path<TaxInvoiceFormProps>,
            isRequired: true,
          },
          {
            titleText: languageTexts.index_text_business_ceo_name,
            formKey: 'brandData.businessCeoName' as Path<TaxInvoiceFormProps>,
            isRequired: true,
          },
          {
            titleText: languageTexts.index_text_business_number,
            formKey: 'brandData.businessNumber' as Path<TaxInvoiceFormProps>,
            isRequired: true,
          },
          {
            titleText: `${languageTexts.index_text_business_email} 1`,
            formKey: 'brandData.businessEmail1' as Path<TaxInvoiceFormProps>,
            isRequired: true,
          },
          {
            titleText: `${languageTexts.index_text_business_email} 2 (${languageTexts.optional})`,
            formKey: 'brandData.businessEmail2' as Path<TaxInvoiceFormProps>,
            isRequired: false,
          },
        ].map((datum, i) => renderInputText({ key:i, ...datum }))}

        <Button
          className={"mt-[40px] py-[15px] flex justify-center"}
          textClassName={"text-[14px] font-bold text-center"}
          buttonColor={"BLACK"}
          textColor={"WHITE"}
          text={languageTexts.done}
          bordered={false}
          onClick={onSubmit}
        />
      </div>
    </div>
  )
}

export default OverlayBrandTaxInvoiceInfoForm
